import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import type {
  CreateCheckoutSessionResponse,
  CreateBillingPortalSessionResponse,
} from '@repo/shared-types';
import { api } from '../lib/api';
import { useSubscription } from '../hooks/useSubscription';

interface Plan {
  id: string;
  name: string;
  priceId: string;
  price: string;
  features: string[];
}

const PLANS: Plan[] = [
  {
    id: 'pro',
    name: 'Pro',
    priceId: import.meta.env.VITE_STRIPE_PRICE_ID_PRO ?? '',
    price: '$9/mo',
    features: ['Unlimited projects', 'Priority support', 'Advanced analytics'],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    priceId: import.meta.env.VITE_STRIPE_PRICE_ID_ENTERPRISE ?? '',
    price: 'Contact us',
    features: [
      'Everything in Pro',
      'Custom integrations',
      'Dedicated support',
    ],
  },
];

function statusColor(
  status: string,
): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'active':
      return 'success';
    case 'trialing':
      return 'warning';
    case 'past_due':
    case 'unpaid':
      return 'error';
    default:
      return 'default';
  }
}

export function BillingPage() {
  const [searchParams] = useSearchParams();
  const showSuccess = searchParams.get('success') === 'true';

  const { subscription, plan, isLoading, error } = useSubscription({ pollUntilActive: showSuccess });
  const isSubscribed = subscription !== null;

  const [checkoutLoadingId, setCheckoutLoadingId] = useState<string | null>(
    null,
  );
  const [portalLoading, setPortalLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function handleUpgrade(pricePlan: Plan) {
    setActionError(null);
    setCheckoutLoadingId(pricePlan.id);
    try {
      const { data } = await api.post<CreateCheckoutSessionResponse>(
        '/api/v1/billing/checkout',
        {
          priceId: pricePlan.priceId,
          successUrl: window.location.origin + '/billing?success=true',
          cancelUrl: window.location.href,
        },
      );
      window.location.href = data.url;
    } catch {
      setActionError('Failed to start checkout. Please try again.');
      setCheckoutLoadingId(null);
    }
  }

  async function handleManageSubscription() {
    setActionError(null);
    setPortalLoading(true);
    try {
      const { data } = await api.post<CreateBillingPortalSessionResponse>(
        '/api/v1/billing/portal',
        { returnUrl: window.location.href },
      );
      window.location.href = data.url;
    } catch {
      setActionError('Failed to open billing portal. Please try again.');
      setPortalLoading(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 3, py: 6 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Billing
      </Typography>

      {showSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Your subscription has been activated successfully. Welcome aboard!
        </Alert>
      )}

      {actionError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load subscription information.
        </Alert>
      )}

      {/* Current plan status */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Plan
          </Typography>

          {isLoading ? (
            <Stack spacing={1}>
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="text" width={150} height={24} />
            </Stack>
          ) : isSubscribed && subscription ? (
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="h5" fontWeight={600} textTransform="capitalize">
                  {plan}
                </Typography>
                <Chip
                  label={subscription.status.replace('_', ' ')}
                  color={statusColor(subscription.status)}
                  size="small"
                />
              </Stack>

              {subscription.currentPeriodEnd && (
                <Typography variant="body2" color="text.secondary">
                  Renews on{' '}
                  {dayjs(subscription.currentPeriodEnd).format('MMMM D, YYYY')}
                </Typography>
              )}

              <Box>
                <Button
                  variant="outlined"
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  startIcon={
                    portalLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {portalLoading ? 'Opening portal…' : 'Manage subscription'}
                </Button>
              </Box>
            </Stack>
          ) : (
            <Typography variant="body1" color="text.secondary">
              You are on the <strong>Free</strong> plan. Upgrade to unlock more
              features.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Pricing table */}
      <Typography variant="h6" gutterBottom>
        Available Plans
      </Typography>

      <Grid container spacing={3}>
        {PLANS.map((pricePlan) => {
          const isCurrentPlan =
            isSubscribed && plan.toLowerCase() === pricePlan.id;
          const isCheckingOut = checkoutLoadingId === pricePlan.id;

          return (
            <Grid key={pricePlan.id} item xs={12} sm={6}>
              <Card
                variant={isCurrentPlan ? 'outlined' : 'elevation'}
                sx={{
                  height: '100%',
                  borderColor: isCurrentPlan ? 'primary.main' : undefined,
                  borderWidth: isCurrentPlan ? 2 : undefined,
                }}
              >
                <CardContent>
                  <Stack spacing={2} height="100%">
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="h6" fontWeight={700}>
                        {pricePlan.name}
                      </Typography>
                      {isCurrentPlan && (
                        <Chip label="Current" color="primary" size="small" />
                      )}
                    </Stack>

                    <Typography variant="h5" fontWeight={600}>
                      {pricePlan.price}
                    </Typography>

                    <Stack component="ul" spacing={0.5} sx={{ pl: 2, m: 0 }}>
                      {pricePlan.features.map((feature) => (
                        <Typography
                          component="li"
                          variant="body2"
                          key={feature}
                        >
                          {feature}
                        </Typography>
                      ))}
                    </Stack>

                    <Box mt="auto" pt={1}>
                      {isCurrentPlan ? (
                        <Button variant="outlined" disabled fullWidth>
                          Current plan
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => handleUpgrade(pricePlan)}
                          disabled={
                            isCheckingOut || checkoutLoadingId !== null
                          }
                          startIcon={
                            isCheckingOut ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : undefined
                          }
                        >
                          {isCheckingOut ? 'Redirecting…' : 'Upgrade'}
                        </Button>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
